'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, User, Sparkles } from 'lucide-react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const GREETING = 'Halo kak! 👋\nkenalkan. Saya STUDO. Dengan kak siapa saya ngobrol?';
const STREAM_SPEED = 30; // ms per character (AI responses are shorter, so slightly faster)
const FOLLOWUP_MSG = 'Ada yang bisa STUDO bantu kak? 😊';
const GOODBYE_MSG = 'Saya akan akhiri percakapan ini ya! makasih, Bye 👋';
const IDLE_FOLLOWUP_MS = 5000;  // 5 detik
const IDLE_GOODBYE_MS = 60000; // 60 detik (1 menit) setelah follow-up

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  // Streaming state
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const hasGreeted = useRef(false);
  const streamAbort = useRef(false);

  // Idle timeout state
  const idleFollowupTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleGoodbyeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFollowedUp = useRef(false);
  const hasSaidBye = useRef(false);
  const userHasReplied = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, streamingText, scrollToBottom]);

  /**
   * Reusable streaming helper — streams text char by char, then calls onDone.
   * Used by both greeting and AI response rendering.
   */
  const streamText = useCallback((text: string, onDone: () => void) => {
    streamAbort.current = false;
    setIsStreaming(true);
    setStreamingText('');
    let i = 0;
    const tick = () => {
      if (streamAbort.current) return;
      if (i < text.length) {
        const char = text[i];
        const isHigh = char.charCodeAt(0) >= 0xD800 && char.charCodeAt(0) <= 0xDBFF;
        const chunk = isHigh ? text.slice(i, i + 2) : char;
        i += chunk.length;
        setStreamingText(text.slice(0, i));
        setTimeout(tick, STREAM_SPEED);
      } else {
        setIsStreaming(false);
        setStreamingText('');
        onDone();
      }
    };
    tick();
  }, []);

  /**
   * Stream multiple reply chunks sequentially (for [SPLIT] responses).
   * Each chunk streams, gets committed to messages, then the next starts.
   */
  const streamReplies = useCallback((replies: string[], startIndex = 0) => {
    if (startIndex >= replies.length) {
      // All chunks done → focus input
      setTimeout(() => inputRef.current?.focus(), 50);
      return;
    }
    const reply = replies[startIndex];
    const msgId = `b-${Date.now()}-${startIndex}`;
    streamText(reply, () => {
      // Commit this chunk to messages
      setMessages(prev => [...prev, {
        id: msgId,
        role: 'assistant',
        content: reply,
        timestamp: new Date(),
      }]);
      // Small delay before next chunk
      if (startIndex + 1 < replies.length) {
        setTimeout(() => streamReplies(replies, startIndex + 1), 300);
      } else {
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    });
  }, [streamText]);

  // ── Idle timer helpers ──
  const clearIdleTimers = useCallback(() => {
    if (idleFollowupTimer.current) { clearTimeout(idleFollowupTimer.current); idleFollowupTimer.current = null; }
    if (idleGoodbyeTimer.current) { clearTimeout(idleGoodbyeTimer.current); idleGoodbyeTimer.current = null; }
  }, []);

  const startIdleFollowup = useCallback(() => {
    // Don't start if user already replied or already followed up
    if (userHasReplied.current || hasFollowedUp.current || hasSaidBye.current) return;
    clearIdleTimers();
    idleFollowupTimer.current = setTimeout(() => {
      if (userHasReplied.current || hasFollowedUp.current) return;
      hasFollowedUp.current = true;
      const followupMsg: Message = {
        id: `followup-${Date.now()}`,
        role: 'assistant',
        content: FOLLOWUP_MSG,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, followupMsg]);

      // Start goodbye timer
      idleGoodbyeTimer.current = setTimeout(() => {
        if (userHasReplied.current || hasSaidBye.current) return;
        hasSaidBye.current = true;
        const byeMsg: Message = {
          id: `bye-${Date.now()}`,
          role: 'assistant',
          content: GOODBYE_MSG,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, byeMsg]);
        // Auto-close widget setelah 2 detik
        setTimeout(() => { setIsOpen(false); }, 2000);
      }, IDLE_GOODBYE_MS);
    }, IDLE_FOLLOWUP_MS);
  }, [clearIdleTimers]);

  // Stream greeting when widget opens for the first time
  useEffect(() => {
    if (isOpen && !hasGreeted.current) {
      hasGreeted.current = true;
      // Small delay before starting to type
      setTimeout(() => {
        streamText(GREETING, () => {
          setMessages([{
            id: 'greeting',
            role: 'assistant',
            content: GREETING,
            timestamp: new Date(),
          }]);
          startIdleFollowup();
        });
      }, 400);
      inputRef.current?.focus();
      setHasNewMessage(false);
    } else if (isOpen) {
      inputRef.current?.focus();
      setHasNewMessage(false);
    }
  }, [isOpen, startIdleFollowup, streamText]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      streamAbort.current = true;
      clearIdleTimers();
    };
  }, [clearIdleTimers]);

  const sendMessage = async (overrideText?: string) => {
    const text = (overrideText || input).trim();
    if (!text || isLoading || isStreaming) return;

    // User replied → cancel all idle timers
    userHasReplied.current = true;
    clearIdleTimers();

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await fetch('/api/home-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [...messages.filter(m => m.id !== 'greeting'), userMsg].map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });
      const data = await res.json();
      const rawReply = data.reply || 'Maaf, saya tidak bisa memproses pesan itu.';
      const replies = rawReply.split('[SPLIT]').map((s: string) => s.trim()).filter(Boolean);

      // Stop loading indicator, then stream each reply chunk
      setIsLoading(false);
      if (!isOpen) {
        // Widget closed → add instantly without streaming
        const newMsgs: Message[] = replies.map((r: string, i: number) => ({
          id: `b-${Date.now()}-${i}`, role: 'assistant', content: r, timestamp: new Date(),
        }));
        setMessages(prev => [...prev, ...newMsgs]);
        setHasNewMessage(true);
      } else {
        // Widget open → stream each chunk with typewriter effect
        streamReplies(replies);
      }
    } catch {
      setIsLoading(false);
      setMessages(prev => [...prev, {
        id: `e-${Date.now()}`, role: 'assistant',
        content: 'Koneksi terputus. Silakan coba lagi.', timestamp: new Date(),
      }]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };



  return (
    <>
      {/* Chat Panel */}
      <div
        ref={panelRef}
        className={`fixed bottom-24 right-6 z-[9999] transition-all duration-300 ease-out
          ${isOpen ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto' : 'opacity-0 translate-y-4 scale-95 pointer-events-none'}`}
        style={{ width: '380px', maxWidth: 'calc(100vw - 32px)' }}
      >
        <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/[0.08]"
          style={{ background: 'rgba(12, 12, 14, 0.98)', backdropFilter: 'blur(20px)' }}>

          {/* Header */}
          <div className="px-5 py-4 flex items-center justify-between border-b border-white/[0.06]"
            style={{ background: 'linear-gradient(135deg, rgba(163,230,53,0.08) 0%, rgba(0,0,0,0) 60%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-lime-500/20 flex items-center justify-center overflow-hidden">
                <img src="/logo.png" alt="STUDO" className="w-6 h-6 object-contain" />
              </div>
              <div>
                <div className="text-[14px] font-semibold text-white tracking-tight">STUDO.ID Assistant</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-lime-400 animate-pulse" />
                  <span className="text-[11px] text-zinc-400">Online</span>
                </div>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/[0.06] transition-colors">
              <X size={16} className="text-zinc-400" />
            </button>
          </div>

          {/* Messages */}
          <div className="h-[360px] overflow-y-auto px-4 py-4 space-y-4 scrollbar-thin"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#27272a transparent' }}>


            {/* Committed messages */}
            {messages.map(msg => (
              <div key={msg.id} className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                {/* Avatar */}
                <div className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5 ${
                  msg.role === 'assistant' ? 'bg-lime-500/20' : 'bg-white/[0.08]'
                }`}>
                  {msg.role === 'assistant'
                    ? <img src="/logo.png" alt="STUDO" className="w-5 h-5 object-contain" />
                    : <User size={14} className="text-zinc-400" />}
                </div>
                {/* Bubble */}
                <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-[13px] leading-relaxed whitespace-pre-wrap ${
                  msg.role === 'assistant'
                    ? 'bg-white/[0.06] text-zinc-200 rounded-tl-md'
                    : 'bg-lime-500 text-zinc-900 rounded-tr-md font-medium'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Streaming bubble — always after committed messages */}
            {isStreaming && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center mt-0.5 bg-lime-500/20 overflow-hidden">
                  <img src="/logo.png" alt="STUDO" className="w-5 h-5 object-contain" />
                </div>
                <div className="max-w-[75%] px-4 py-2.5 rounded-2xl rounded-tl-md text-[13px] leading-relaxed whitespace-pre-wrap bg-white/[0.06] text-zinc-200">
                  {streamingText}
                  <span className="inline-block w-[2px] h-[14px] bg-lime-400 ml-0.5 align-middle animate-pulse" />
                </div>
              </div>
            )}

            {/* Typing indicator */}
            {isLoading && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-lime-500/20 flex items-center justify-center mt-0.5 overflow-hidden">
                  <img src="/logo.png" alt="STUDO" className="w-5 h-5 object-contain" />
                </div>
                <div className="bg-white/[0.06] px-4 py-3 rounded-2xl rounded-tl-md">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-2 bg-white/[0.04] rounded-xl px-4 py-2 border border-white/[0.08] focus-within:border-lime-500/40 transition-colors">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ketik pesan..."
                disabled={isLoading || isStreaming}
                className="flex-1 bg-transparent text-[13px] text-white placeholder:text-zinc-500 outline-none disabled:opacity-50"
              />
              <button onClick={() => sendMessage()} disabled={!input.trim() || isLoading || isStreaming}
                className="w-8 h-8 rounded-lg flex items-center justify-center bg-lime-500 hover:bg-lime-400 disabled:opacity-30 disabled:hover:bg-lime-500 transition-colors">
                <Send size={14} className="text-zinc-900" />
              </button>
            </div>
            <p className="text-[10px] text-zinc-600 text-center mt-2">Powered by STUDO.ID AI</p>
          </div>
        </div>
      </div>

      {/* Floating toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-black/40 transition-all duration-200 hover:scale-105 active:scale-95 group"
        style={{ background: isOpen ? 'rgba(39,39,42,0.95)' : 'linear-gradient(135deg, #a3e635, #84cc16)' }}
        aria-label={isOpen ? 'Tutup chat' : 'Buka chat'}
      >
        {isOpen
          ? <X size={22} className="text-white" />
          : <MessageCircle size={22} className="text-zinc-900" />}

        {/* New message badge */}
        {hasNewMessage && !isOpen && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center animate-bounce">
            1
          </span>
        )}

        {/* Pulse ring */}
        {!isOpen && (
          <span className="absolute inset-0 rounded-full border-2 border-lime-400/40 animate-ping pointer-events-none" />
        )}
      </button>
    </>
  );
}
